import { createContext, useContext, useEffect, useCallback, useMemo, useRef, type FC, type ReactNode } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import { hasAnyWorkspaceView } from '@/shared/workspaceViewAccess';
import { useMultiProject } from './MultiProjectContext';
import type { ProjectDataContextType } from '@/features/projects/contexts/ProjectDataContext/types';
import { LoadingState } from '@/features/projects/contexts/ProjectDataContext/components/LoadingState';
import { ErrorState } from '@/features/projects/contexts/ProjectDataContext/components/ErrorState';
import { useProjectState } from '@/features/projects/contexts/ProjectDataContext/hooks/useProjectState';
import { useFilteredNodes } from '@/features/projects/contexts/ProjectDataContext/hooks/useFilteredNodes';
import { useDataLoading } from '@/features/projects/contexts/ProjectDataContext/hooks/useDataLoading';
import { useAutoSave } from '@/features/projects/contexts/ProjectDataContext/hooks/useAutoSave';
import { useNodeOperations } from '@/features/projects/contexts/ProjectDataContext/hooks/useNodeOperations';
import { useTreeOperations } from '@/features/projects/contexts/ProjectDataContext/hooks/useTreeOperations';
import { useOperationCapture } from '@/features/projects/contexts/ProjectDataContext/hooks/useOperationCapture';
import { useUndoRedo } from '@/features/projects/contexts/ProjectDataContext/hooks/useUndoRedo';
import { useImportExport } from '@/features/projects/contexts/ProjectDataContext/hooks/useImportExport';
import { useSyncUtilities } from '@/features/projects/contexts/ProjectDataContext/hooks/useSyncUtilities';
import { useUIStateOperations } from '@/features/projects/contexts/ProjectDataContext/hooks/useUIStateOperations';
import { useHelperFunctions } from '@/features/projects/contexts/ProjectDataContext/hooks/useHelperFunctions';
import { useCRUDOperations } from '@/features/projects/contexts/ProjectDataContext/hooks/useCRUDOperations';
import { useHistoryManagement } from '@/features/projects/contexts/ProjectDataContext/hooks/useHistoryManagement';
import { useInitializationEffects } from '@/features/projects/contexts/ProjectDataContext/hooks/useInitializationEffects';
import { useContextValue } from '@/features/projects/contexts/ProjectDataContext/hooks/useContextValue';
import { useUndoHandlersInit } from '@/features/projects/contexts/ProjectDataContext/hooks/useUndoHandlersInit';
import { useWebSocketManager } from '@/features/projects/contexts/ProjectDataContext/hooks/useWebSocketManager';
import { useServerGanttTreePreferences } from '@/features/projects/contexts/ProjectDataContext/hooks/useServerGanttTreePreferences';
import { viewStates, uiState } from '@/features/projects/contexts/ProjectDataContext/constants';
import { resolveAuthoritativeProjectData } from '@/features/projects/contexts/ProjectDataContext/hooks/authoritativeProjectAdoption';
import { sanitizeProjectData } from '@/shared/utils/projectDataUtils';
import { log } from '@/shared/utils/logger';
import { isExpectedSubscribedRefreshGap } from './utils/subscribedRefreshErrors';
import { isPersistedProjectId } from './utils/syntheticDefaultProject';
import type { VisibilityTier } from '@/shared/types/projectData';

const ProjectDataContext = createContext<ProjectDataContextType | undefined>(undefined);

export const ProjectDataProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { user, hasPermission } = useAuth();
  const { aggregatedData, visibleProjects, selectedProjectIds, visibleProjectIds, updateProjectInCache, activeProjectId, refreshProject } = useMultiProject();
  
  const {
    projectData, setProjectData, error, setError, isLoading, setIsLoading,
    ws, sessionId, wsState, setWsState, syncState, setSyncState,
    wsManager, pollTimer, expandedNodeIds, setExpandedNodeIds,
    selectedNodeIds, setSelectedNodeIds, filters, setFilters,
    searchQuery, setSearchQueryState, sortBy, setSortByState,
    isCompactView, setIsCompactViewState, snapshotHistory,
    undoState, setUndoState, hasUnsavedChanges, setHasUnsavedChanges, beginUnconfirmedWrite, getWriteRevision,
    isUndoRedoInProgress, setIsUndoRedoInProgress
  } = useProjectState();
  
  const {
    updateUndoStateFromSnapshots, undo, redo, jumpToHistoryPosition,
    getUndoHistoryList
  } = useUndoRedo({
    setError,
    setIsUndoRedoInProgress,
    setUndoState
  });
  
  const { loadProjectDataFromServer } = useDataLoading({
    setProjectData, setIsLoading, setError, setExpandedNodeIds,
    snapshotHistory, updateUndoStateFromSnapshots
  });
  
  // Wrap refreshProject to refresh the current/active project when WS triggers reload
  const handleServerRefresh = useCallback(() => {
    const projectIdToRefresh = activeProjectId || aggregatedData?.projectId || projectData?.projectId;
    if (projectIdToRefresh && refreshProject) {
      log.info('ProjectDataContext', 'WS triggered refresh for project:', projectIdToRefresh);
      refreshProject(projectIdToRefresh);
    } else {
      log.warn('ProjectDataContext', 'No project ID available for WS refresh');
    }
  }, [activeProjectId, aggregatedData?.projectId, projectData?.projectId, refreshProject]);
  
  const singleVisibleProject = useMemo(
    () => (visibleProjects.length === 1 ? visibleProjects[0] : null),
    [visibleProjects]
  );
  const authoritativeProjectData = useMemo(
    () =>
      resolveAuthoritativeProjectData({
        singleProjectData: singleVisibleProject,
        aggregatedData,
      }),
    [singleVisibleProject, aggregatedData]
  );
  const liveProjectData = useMemo(
    () => authoritativeProjectData || projectData,
    [authoritativeProjectData, projectData]
  );
  const projectVisibilityDefaults = useMemo(
    () =>
      visibleProjects.reduce<Record<string, VisibilityTier | undefined>>((acc, visibleProject) => {
        acc[visibleProject.projectId] = visibleProject.metadata?.settings?.visibilityTierDefault;
        return acc;
      }, {}),
    [visibleProjects]
  );
  const projectOwnerIds = useMemo(
    () =>
      visibleProjects.reduce<Record<string, string | undefined>>((acc, visibleProject) => {
        if (typeof visibleProject.owner === 'string' && visibleProject.owner) {
          acc[visibleProject.projectId] = visibleProject.owner;
        }
        return acc;
      }, {}),
    [visibleProjects]
  );

  // An account that may open no workspace view sees only "Workspace setup needed";
  // nothing renders project data there, so it keeps no project socket or subscription.
  const realtimeEnabled = useMemo(() => hasAnyWorkspaceView(user, hasPermission), [user, hasPermission]);

  // Initialize WebSocket connection
  const { wsManager: wsManagerInstance, connectionProjectId } = useWebSocketManager({
    user,
    projectData: liveProjectData,
    activeProjectId,
    visibleProjectIds,
    wsState,
    sessionId,
    setProjectData,
    setWsState: (nextState) => setWsState(nextState),
    setSyncState: (nextState) => setSyncState((currentState) => ({ ...currentState, ...nextState })),
    setError,
    setExpandedNodeIds,
    loadProjectDataFromServer: handleServerRefresh,  // Use real refresh instead of stub
    updateProjectInCache,  // Pass the cache update function
    refreshProject,  // Pass refreshProject directly so WS can call it with message.projectId
    projectVisibilityDefaults,
    projectOwnerIds,
    realtimeEnabled,
  });
  
  // Update the wsManager ref with the actual instance
  if (wsManagerInstance.current) {
    wsManager.current = wsManagerInstance.current;
  }

  const lastSubscriptionRequestRef = useRef<{ manager: unknown | null; key: string; ids: Set<string> }>({
    manager: null,
    key: '',
    ids: new Set(),
  });
  // The synthetic empty-catalog project has no stored preferences or server version to check.
  const serverViewPreferenceProjectId = isPersistedProjectId(singleVisibleProject?.projectId)
    ? singleVisibleProject.projectId
    : null;

  // CRITICAL: Subscribe to ALL currently visible projects + active project
  // The hook records this complete set before deferred connection; reconcile
  // again here while refreshing newly visible projects.
  useEffect(() => {
    const manager = wsManager.current;
    if (!manager || manager.getPrincipalContext().userId !== user?.id) return;
    
    // Collect ALL project IDs that should receive live updates
    const projectsToSubscribe = new Set<string>();
    
    // Add all actually visible projects
    if (realtimeEnabled && Array.isArray(visibleProjectIds)) {
      visibleProjectIds.forEach(pid => {
        if (pid && pid !== 'default' && typeof pid === 'string') {
          projectsToSubscribe.add(pid);
        }
      });
    }
    
    // Add active project
    if (realtimeEnabled && activeProjectId && activeProjectId !== 'default') {
      projectsToSubscribe.add(activeProjectId);
    }
    
    const subscriptionIds = Array.from(projectsToSubscribe).sort();
    const subscriptionKey = `${user?.id || ''}:${manager.getPrincipalContext().generation}:${subscriptionIds.join(',')}`;
    const previousRequest = lastSubscriptionRequestRef.current;
    const previousIds =
      previousRequest.manager === manager
        ? previousRequest.ids
        : new Set<string>();

    if (
      previousRequest.manager === manager &&
      previousRequest.key === subscriptionKey
    ) {
      return;
    }


    manager.reconcileSubscriptions(subscriptionIds, manager.getPrincipalContext());

    subscriptionIds.forEach(pid => {
      if (!previousIds.has(pid)) {
        if (refreshProject) {
          void refreshProject(pid).catch((error) => {
            if (isExpectedSubscribedRefreshGap(error)) {
              log.debug('ProjectDataContext', 'Skipped subscribed project refresh (superseded or auth not ready)', {
                projectId: pid,
              });
              return;
            }
            log.warn('ProjectDataContext', 'Failed to refresh newly subscribed project', {
              projectId: pid,
              error,
            });
          });
        }
      }
    });

    lastSubscriptionRequestRef.current = {
      manager,
      key: subscriptionKey,
      ids: new Set(subscriptionIds),
    };
    if (subscriptionIds.length > 0) {
      log.debug('ProjectDataContext', '🔔 [WS SUBSCRIBE] Subscribing to projects:', subscriptionIds);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- wsManager is a ref; .current is intentionally read inside the effect
  }, [user?.id, visibleProjectIds, activeProjectId, realtimeEnabled, wsManager.current]);
  
  const fetchDataFromServer = () => {
    if (singleVisibleProject) {
      const sanitizedData = sanitizeProjectData(singleVisibleProject);
      setProjectData(sanitizedData);
      setIsLoading(false);
    } else if (aggregatedData) {
      // Use aggregated data from MultiProjectContext
      // IMPORTANT: Sanitize data to fix any circular references or data integrity issues
      const sanitizedData = sanitizeProjectData(aggregatedData);
      // No transformation needed - backend sends data in correct format
      setProjectData(sanitizedData);
      setIsLoading(false);
    } else if (!selectedProjectIds || selectedProjectIds.length === 0 || selectedProjectIds[0] === 'default') {
      // Fallback to loading default project
      loadProjectDataFromServer();
    } else {
      // No data available
      setIsLoading(false);
    }
  };
  
  const { localCacheSaveFailed } = useAutoSave({ projectData, expandedNodeIds, isCompactView, setHasUnsavedChanges });

  useServerGanttTreePreferences({
    projectId: serverViewPreferenceProjectId,
    expandedNodeIds,
    isCompactView,
    setExpandedNodeIds,
    setIsCompactViewState,
  });
  
  const { checkServerVersion, syncWithServer, reconnectWebSocket, retrySyncRecovery } = useSyncUtilities({
    projectId: serverViewPreferenceProjectId,
    projectIds: Array.from(visibleProjectIds),
    refreshProject,
    hasUnconfirmedWrites: hasUnsavedChanges,
    getWriteRevision,
    reconnectProjectId: connectionProjectId,
    syncState, setSyncState, wsManager, sessionId, loadProjectDataFromServer
  });
  
  const filteredNodes = useFilteredNodes({
    projectData, filters, searchQuery, sortBy, user, hasPermission
  });
  
  const { applyOp } = useNodeOperations({
    beginUnconfirmedWrite,
    projectData, expandedNodeIds, selectedNodeIds, filters, searchQuery,
    sortBy, isCompactView, user, viewStates, uiState, sessionId,
    snapshotHistory, ws, wsManager, setProjectData, setError, updateUndoStateFromSnapshots,
    updateProjectInCache
  });
  
  const { importProjectData } = useImportExport({ setError, sessionId });

  const { captureOperation } = useOperationCapture({ sessionId });
  
  const {
    toggleExpanded, expandAll: expandAllFromHook,
    collapseAll, selectNode
  } = useTreeOperations({
    expandedNodeIds,
    selectedNodeIds,
    setExpandedNodeIds,
    setSelectedNodeIds,
    captureOperation
  });

  const {
    instrumentedSetFilters, setSearchQuery, setSortBy, setIsCompactView
  } = useUIStateOperations({
    filters, searchQuery, sortBy, isCompactView, setFilters,
    setSearchQueryState, setSortByState, setIsCompactViewState, captureOperation
  });
  
  const { nodeMap, treeNodes, getRenderableNodes, getNode, getVisibleNodes } = useHelperFunctions({
    projectData, filteredNodes, expandedNodeIds
  });

  const expandAll = useCallback(() => {
    expandAllFromHook(treeNodes);
  }, [expandAllFromHook, treeNodes]);
  
  const {
    moveNodeTo, updateNode, batchUpdateNodes, moveGroup,
    deleteNode, deleteNodes, createNode, updateProgress
  } = useCRUDOperations({ projectData, applyOp });
  
  const { clearHistory, getSnapshotPerformanceReport, configureSnapshotHistory } = useHistoryManagement({
    projectData, setError, snapshotHistory
  });
  
  useInitializationEffects({
    authoritativeProjectData, selectedProjectIds, user, wsState,
    setProjectData, setIsLoading, setError,
    setIsCompactView: setIsCompactViewState,
    snapshotHistory, sessionId, wsManager, pollTimer,
    updateUndoStateFromSnapshots, fetchDataFromServer,
    syncWithServer
  });
  
  useUndoHandlersInit({
    applyOp, setSelectedNodeIds, setExpandedNodeIds, setFilters,
    setSearchQueryState, setSortByState, setIsCompactViewState
  });
  
  // IMPORTANT: All hooks must be called before any conditional returns (rules-of-hooks)
  const value = useContextValue({
    projectData, treeNodes, nodeMap, expandedNodeIds, selectedNodeIds,
    filteredNodes, applyOp, captureOperation, moveNodeTo, updateNode,
    batchUpdateNodes, moveGroup, deleteNode, deleteNodes, createNode,
    getNode, getVisibleNodes, toggleExpanded, selectNode, expandAll,
    collapseAll, getRenderableNodes, filters,
    instrumentedSetFilters, searchQuery, setSearchQuery, sortBy,
    setSortBy, isCompactView, setIsCompactView, undo, redo,
    canUndo: undoState.canUndo, canRedo: undoState.canRedo,
    clearHistory, jumpToHistoryPosition, getUndoHistoryList,
    undoState, isUndoRedoInProgress, importProjectData, updateProgress,
    wsState, syncState, reconnectWebSocket, retrySyncRecovery, checkServerVersion,
    syncWithServer, hasUnsavedChanges, setHasUnsavedChanges, localCacheSaveFailed,
    getSnapshotPerformanceReport, configureSnapshotHistory
  });

  // Don't show loading state when using multi-project context
  // The multi-project context handles its own loading state
  if (isLoading && aggregatedData === undefined) return <LoadingState />;
  if (error && !projectData && aggregatedData === undefined) return <ErrorState error={error} onRetry={fetchDataFromServer} />;
  // Don't return null - provide context even with no data so views can show empty states

  return <ProjectDataContext.Provider value={value}>{children}</ProjectDataContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components -- co-located hook is conventional for React context files
export const useProjectData = (): ProjectDataContextType => {
  const context = useContext(ProjectDataContext);
  if (context === undefined) {
    throw new Error('useProjectData must be used within a ProjectDataProvider');
  }
  return context;
};
